import { useCallback } from 'react'
import { trackGoal } from '@lunary/ui-web/analytics'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LedgerType } from '@lunary/app-core/lunar-history'
import type {
  CreateGiftDto,
  GiftListResponseDto,
  GiftResponseDto,
  LunarLedgerResponseDto,
  LunarPaymentResponseDto,
  LunarsResponseDto,
  PublicGiftResponseDto,
  YookassaPaymentResponseDto,
} from '@lunary/api-client'
import { api } from '../../shared/api/client'

export const LUNARS_KEY = ['lunars'] as const
const GIFTS_KEY = ['lunars', 'gifts'] as const

export function useLunars(enabled: boolean) {
  return useQuery({
    queryKey: LUNARS_KEY,
    queryFn: () => api.get<LunarsResponseDto>('/users/me/lunars'),
    enabled,
    retry: false,
  })
}

function ledgerUrl(type: LedgerType | null, before: string | null): string {
  const q = new URLSearchParams({ limit: '30' })
  if (type) q.set('type', type)
  if (before) q.set('before', before)
  return `/users/me/lunars/ledger?${q.toString()}`
}

export function useLedger(enabled: boolean, type: LedgerType | null) {
  return useInfiniteQuery({
    queryKey: ['lunars', 'ledger', type],
    queryFn: ({ pageParam }) => api.get<LunarLedgerResponseDto>(ledgerUrl(type, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  })
}

export function useMyGifts(enabled: boolean) {
  return useQuery({
    queryKey: GIFTS_KEY,
    queryFn: () => api.get<GiftListResponseDto>('/users/me/gifts?limit=50'),
    enabled,
    retry: false,
  })
}

export function useRefreshLunars() {
  const qc = useQueryClient()
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: LUNARS_KEY }).then(() =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['profile'] }),
        qc.invalidateQueries({ queryKey: ['orbit'] }),
        qc.invalidateQueries({ queryKey: ['daily-pick', 'today'] }),
      ]),
    )
  }, [qc])
}

export function useCreateGift() {
  const refresh = useRefreshLunars()
  return useMutation({
    mutationFn: (input: CreateGiftDto) => api.post<GiftResponseDto>('/users/me/gifts', input),
    onSuccess: (data) => {
      trackGoal('gift_created', { format: data.format, price: data.price })
      refresh()
    },
  })
}

export function useCancelGift() {
  const refresh = useRefreshLunars()
  return useMutation({
    mutationFn: (id: string) => api.delete<GiftResponseDto>(`/users/me/gifts/${encodeURIComponent(id)}`),
    onSuccess: refresh,
  })
}

export function createYookassaPayment(packId: string): Promise<YookassaPaymentResponseDto> {
  return api.post<YookassaPaymentResponseDto>('/users/me/lunars/yookassa-payment', { pack_id: packId })
}

export function fetchPayment(id: string): Promise<LunarPaymentResponseDto> {
  return api.get<LunarPaymentResponseDto>(`/users/me/lunars/payments/${encodeURIComponent(id)}`)
}

export function usePublicGift(code: string) {
  return useQuery({
    queryKey: ['gift', code],
    queryFn: () => api.get<PublicGiftResponseDto>(`/gifts/${encodeURIComponent(code)}`),
    retry: false,
  })
}

export function useClaimGift(code: string) {
  const qc = useQueryClient()
  const refresh = useRefreshLunars()
  return useMutation({
    mutationFn: () => api.post<PublicGiftResponseDto>(`/gifts/${encodeURIComponent(code)}/claim`, undefined),
    onSuccess: (data) => {
      trackGoal('gift_claimed', { format: data.format })
      qc.setQueryData(['gift', code], data)
      refresh()
    },
  })
}

export function giftLink(code: string): string {
  return `${window.location.origin}/gift/${code}`
}
